import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine.ts';
import { type SharedEventLoop } from '../../event-loop.ts';
import { type SelectionUiInteractionHandler } from '../../selection-ui-interaction.ts';
import { type SelectionInput } from '../../types.ts';
import { type TableNavigationAdapter } from '../table-navigation-adapter.ts';

import { createTableTabNavigationKeyDownHandler } from './handle-table-tab-navigation-key-down.ts';

/**
 * Creates the table-specific Tab keyboard input.
 *
 * This input subscribes to the shared synchronous keyboard loop so Tab can be
 * captured before browser focus moves into legacy table controls. It delegates
 * table geometry to the adapter instead of importing frontend table layout code
 * into the selections package.
 */
export function createTableTabNavigationInput(
  machine: SelectionsMachine,
  sharedLoop: SharedEventLoop,
  options: {
    adapter: TableNavigationAdapter;
    onSelectionUiInteraction?: SelectionUiInteractionHandler;
  }
): SelectionInput {
  const handler = createTableTabNavigationKeyDownHandler(
    options.adapter,
    options.onSelectionUiInteraction
  );
  let unsubscribe: (() => void) | undefined;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribeSyncKeyboardDown(
        (capture: KeyboardDownCapture) => {
          if (capture.properties.consumed) {
            return;
          }

          handler(capture, machine);

          if (capture.properties.consumed) {
            capture.properties.event.stopPropagation?.();
          }
        }
      );
    },

    dispose() {
      unsubscribe?.();
    }
  };
}
